import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { Gauge, Loader2, ShieldCheck } from "lucide-react";
import { ForgotPasswordDialog } from "@/components/ForgotPasswordDialog";
import { MfaChallenge } from "@/components/MfaChallenge";

const HERO = "https://static.prod-images.emergentagent.com/jobs/30c0415f-ce02-479a-8a3a-0dea62d22e5c/images/c68a9b9707a62681c2da479e3791d1b413b18d05865e0849df180e7e920c7de3.jpeg";

export default function Login() {
  const { login, applyUser, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);

  const homeFor = (role) => (role === "admin" ? "/admin" : role === "technician" ? "/technician" : "/");

  useEffect(() => {
    if (user) navigate(homeFor(user.role), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok && res.mfa) {
      setChallenge({ ...res, email: res.email || email.trim() });
    } else if (!res.ok) {
      setError(res.error);
    }
  };

  const handleMfaComplete = (u) => {
    applyUser(u);
    navigate(homeFor(u.role), { replace: true });
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden" data-testid="login-page">
      <img src={HERO} alt="Technician and warranty administrator using Warranty Compliance Pro" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#431407]/70 via-[#431407]/10 to-[#431407]/35" />

      {/* Brand — top left */}
      <div className="absolute left-5 top-5 flex items-center gap-3 lg:left-10 lg:top-8">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary shadow-lg shadow-primary/40 lg:h-11 lg:w-11">
          <Gauge className="h-5 w-5 text-white lg:h-6 lg:w-6" />
        </div>
        <div>
          <p className="font-heading text-base font-extrabold uppercase leading-none tracking-tight text-white lg:text-xl">Warranty Compliance Pro</p>
          <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-orange-200/80 lg:text-[10px]">Vehicle Repair Order Auditing</p>
        </div>
      </div>

      {/* Headline — bottom left */}
      <div className="absolute bottom-6 left-5 z-10 max-w-md pr-4 lg:bottom-10 lg:left-10">
        <h2 className="font-heading text-2xl font-extrabold uppercase leading-tight tracking-tight text-white drop-shadow-lg sm:text-3xl lg:text-4xl">
          Validate every warranty claim before you submit
        </h2>
        <p className="mt-2.5 text-xs leading-relaxed text-orange-50/90 drop-shadow lg:text-sm">
          Compliance checks on every repair order — before it's submitted.
        </p>
      </div>

      {/* Sign-in card — right side */}
      <div className="absolute right-4 top-4 lg:right-6 lg:top-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-[15.5rem] rounded-xl bg-[#fff7ed]/95 p-4 shadow-2xl ring-1 ring-black/5 backdrop-blur-md"
        >
          {challenge ? (
            <MfaChallenge challenge={challenge} onComplete={handleMfaComplete} onBack={() => setChallenge(null)} />
          ) : (
            <>
              <p className="mb-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-orange-600">
                <ShieldCheck className="h-3 w-3" /> Secure Sign In
              </p>
              <h1 className="font-heading text-lg font-extrabold uppercase tracking-tight text-slate-900">Welcome back</h1>
              <p className="mt-1 text-xs leading-snug text-slate-600">Private workspace. Access granted by the administrator.</p>

              <form onSubmit={submit} className="mt-4 space-y-3">
                <div>
                  <Label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Email</Label>
                  <Input type="email" data-testid="login-email-input" className="h-9 border-slate-300 bg-white text-sm text-slate-900 placeholder:text-slate-400" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required />
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <Label className="block font-mono text-[10px] uppercase tracking-wider text-slate-500">Password</Label>
                    <ForgotPasswordDialog />
                  </div>
                  <Input type="password" data-testid="login-password-input" className="h-9 border-slate-300 bg-white text-sm text-slate-900 placeholder:text-slate-400" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
                </div>
                {error && <p data-testid="login-error" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="login-submit-button" className="h-9 w-full bg-orange-500 text-white hover:bg-orange-600">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Sign In
                </Button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
